import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  ErrorOutline,
  InfoOutlined,
  Refresh,
  WarningAmberOutlined,
} from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import apiClient from "@/utils/api-client";
import { trackSentryException } from "@/utils/helpers";

export type ValidationSeverity = "blocker" | "warning" | "info";

export interface ValidationIssue {
  id: string;
  severity: ValidationSeverity;
  category: string;
  title: string;
  description: string;
  suggestion?: string | null;
  quoted_excerpt?: string | null;
}

export interface ValidationResponse {
  issues: ValidationIssue[];
  model_name?: string | null;
}

/**
 * Calls the backend LLM pre-flight that scans a test's markdown for typical
 * automation issues (missing credentials, non-dynamic data, requested
 * platform features we don't support, etc). Resolves with the issues list;
 * never throws — failures surface as a single "info" issue so the run can
 * still proceed.
 */
export const fetchTestContentValidation = async (params: {
  organisationId: string;
  markdown: string;
  platform?: string;
}): Promise<ValidationResponse> => {
  const response = await apiClient.post<ValidationResponse>(
    "/validate_test_content",
    {
      org_id: params.organisationId,
      markdown: params.markdown,
      platform: params.platform ?? null,
    }
  );
  return {
    issues: Array.isArray(response.data?.issues) ? response.data.issues : [],
    model_name: response.data?.model_name ?? null,
  };
};

const SEVERITY_ORDER: Record<ValidationSeverity, number> = {
  blocker: 0,
  warning: 1,
  info: 2,
};

const severityChipColor = (
  severity: ValidationSeverity
): "error" | "warning" | "info" => {
  if (severity === "blocker") return "error";
  if (severity === "warning") return "warning";
  return "info";
};

const SeverityIcon = ({ severity }: { severity: ValidationSeverity }) => {
  if (severity === "blocker") return <ErrorOutline color="error" />;
  if (severity === "warning") return <WarningAmberOutlined color="warning" />;
  return <InfoOutlined color="info" />;
};

interface Props {
  open: boolean;
  initialMarkdown: string;
  initialIssues: ValidationIssue[];
  organisationId: string;
  platform?: string;
  /** Closes the dialog without running. */
  onCancel: () => void;
  /**
   * Closes and proceeds. If the user edited the markdown, the (possibly
   * updated) value is passed back so the editor can swap its content in
   * before the test runs.
   */
  onProceed: (params: { updatedMarkdown: string; markdownChanged: boolean }) => void;
}

const LlmValidationDialog: React.FC<Props> = ({
  open,
  initialMarkdown,
  initialIssues,
  organisationId,
  platform,
  onCancel,
  onProceed,
}) => {
  const [markdown, setMarkdown] = useState(initialMarkdown);
  const [issues, setIssues] = useState<ValidationIssue[]>(initialIssues);
  const [revalidating, setRevalidating] = useState(false);
  const [revalidationError, setRevalidationError] = useState<string | null>(
    null
  );

  // Reset state whenever the dialog is freshly opened so a previous session
  // doesn't bleed in. We avoid replacing state on every prop change (the
  // user might have started editing) — only the `open` transition resets.
  useEffect(() => {
    if (open) {
      setMarkdown(initialMarkdown);
      setIssues(initialIssues);
      setRevalidationError(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedIssues = useMemo(
    () =>
      [...issues].sort(
        (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
      ),
    [issues]
  );

  const blockerCount = useMemo(
    () => issues.filter((i) => i.severity === "blocker").length,
    [issues]
  );

  const markdownChanged = markdown !== initialMarkdown;

  const handleRevalidate = useCallback(async () => {
    setRevalidating(true);
    setRevalidationError(null);
    try {
      const response = await fetchTestContentValidation({
        organisationId,
        markdown,
        platform,
      });
      setIssues(response.issues);
    } catch (e) {
      trackSentryException(e);
      setRevalidationError(
        (e as any)?.response?.data?.detail ??
          (e as Error)?.message ??
          "Re-validation failed."
      );
    } finally {
      setRevalidating(false);
    }
  }, [markdown, organisationId, platform]);

  const proceedLabel = blockerCount > 0 ? "Run Anyway" : "Run Test";

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullScreen
      // Keep the dialog mounted only while open — re-mounting on each open is
      // intentional so the inner state above resets via `open` effect cleanly.
      keepMounted={false}
    >
      <AppBar position="relative" color="default" elevation={1}>
        <Toolbar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Review test before running</Typography>
            <Typography variant="caption" color="text.secondary">
              {issues.length === 0
                ? "No issues detected — you can run the test."
                : `${issues.length} potential issue${
                    issues.length === 1 ? "" : "s"
                  } found in your test content.`}
            </Typography>
          </Box>
          <Tooltip title="Re-run validation against the current content">
            <span>
              <LoadingButton
                onClick={handleRevalidate}
                loading={revalidating}
                startIcon={<Refresh />}
                sx={{ mr: 1 }}
              >
                Re-validate
              </LoadingButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ mr: 1 }}
            disabled={revalidating}
          >
            Back to editor
          </Button>
          <Button
            variant="contained"
            color={blockerCount > 0 ? "error" : "primary"}
            onClick={() =>
              onProceed({ updatedMarkdown: markdown, markdownChanged })
            }
            disabled={revalidating}
          >
            {proceedLabel}
          </Button>
          <IconButton onClick={onCancel} sx={{ ml: 1 }} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          flex: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          p: 2,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Test content (markdown)
          </Typography>
          <TextField
            multiline
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            // We use a textarea here rather than the TipTap editor so the
            // user sees and edits the raw markdown the LLM was just shown,
            // which makes it obvious which excerpts the issues refer to.
            sx={{
              flex: 1,
              "& .MuiInputBase-root": {
                height: "100%",
                alignItems: "flex-start",
                fontFamily: "ui-monospace, Menlo, monospace",
                fontSize: 13,
              },
              "& textarea": { height: "100% !important" },
            }}
          />
          {markdownChanged && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              You've edited the test. Click "Re-validate" to refresh the issue
              list, or "Run Test" to apply your edits and run.
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "auto",
            pr: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Identified issues
          </Typography>
          {revalidationError != null && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {revalidationError}
            </Alert>
          )}
          {issues.length === 0 && !revalidating && (
            <Alert severity="success">
              <AlertTitle>Looks good</AlertTitle>
              The validator didn't find any predictable issues in this test.
            </Alert>
          )}
          {revalidating && issues.length === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "text.secondary",
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2">Validating...</Typography>
            </Box>
          )}
          <Stack spacing={1.5}>
            {sortedIssues.map((issue) => (
              <Alert
                key={issue.id}
                icon={<SeverityIcon severity={issue.severity} />}
                severity={severityChipColor(issue.severity)}
                variant="outlined"
              >
                <AlertTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{issue.title}</span>
                  <Chip
                    size="small"
                    label={issue.category}
                    sx={{ textTransform: "lowercase" }}
                  />
                </AlertTitle>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {issue.description}
                </Typography>
                {issue.quoted_excerpt && (
                  <Box
                    component="pre"
                    sx={{
                      mt: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: 12,
                      whiteSpace: "pre-wrap",
                      overflowX: "auto",
                    }}
                  >
                    {issue.quoted_excerpt}
                  </Box>
                )}
                {issue.suggestion && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, fontStyle: "italic" }}
                  >
                    Suggestion: {issue.suggestion}
                  </Typography>
                )}
              </Alert>
            ))}
          </Stack>
        </Box>
      </Box>
    </Dialog>
  );
};

export default LlmValidationDialog;
